/**
 * SERVER TIME.
 *
 * Every gameplay timestamp in this game is server time (ROUNDTABLE.md, A-10).
 * A deer's spawn record says `t0: 1755300123456`, and that number means
 * something only on the server's clock. A phone whose clock is four seconds
 * fast would otherwise render the herd four seconds into its future and aim at
 * animals the server does not believe are there.
 *
 * ── Why the minimum, not the mean ───────────────────────────────────────────
 * The offset is estimated from `ping`/`pong` round trips. Averaging the samples
 * is the obvious thing and the wrong thing: every sample is inflated by
 * whatever queuing delay that particular packet met, and queuing delay is
 * strictly non-negative and heavily right-skewed on mobile networks. The
 * MINIMUM-RTT sample is the one that queued least, so it carries the least
 * bias — this is the same reason NTP keeps the best of a burst rather than the
 * average of one. On a phone that briefly loses signal, the mean would be
 * dragged by hundreds of milliseconds; the minimum is untouched.
 *
 * A rolling window is kept so the estimate can recover when the network path
 * genuinely changes (wifi → LTE), rather than being pinned forever by one lucky
 * packet from ten minutes ago.
 */

interface Sample {
  rtt: number;
  offset: number;
  at: number;
}

const WINDOW = 12;
/** Samples older than this are dropped, so a path change can be tracked. */
const MAX_AGE_MS = 120_000;

class Clock {
  private samples: Sample[] = [];
  private best: Sample | null = null;

  /** Best-estimate round trip, ms. Shown in the HUD's connection pip. */
  rtt = 0;
  /** True once at least one sample has landed. */
  synced = false;

  /**
   * Fold in one round trip.
   * `sentAt` and `recvAt` are local `Date.now()`; `serverAt` is what the server
   * stamped when it replied.
   */
  sample(sentAt: number, serverAt: number, recvAt: number) {
    const rtt = recvAt - sentAt;
    if (rtt < 0 || rtt > 10_000) return;
    // The server's reply was written at roughly the midpoint of the round trip.
    const offset = serverAt - (sentAt + rtt / 2);
    const s: Sample = { rtt, offset, at: recvAt };

    this.samples.push(s);
    if (this.samples.length > WINDOW) this.samples.shift();

    const now = recvAt;
    this.samples = this.samples.filter((x) => now - x.at < MAX_AGE_MS);

    let best = this.samples[0]!;
    for (const x of this.samples) if (x.rtt < best.rtt) best = x;
    this.best = best;
    this.rtt = Math.round(best.rtt);
    this.synced = true;
  }

  /**
   * Adopt the server's clock outright. Used for the very first `hello`, so the
   * herd is roughly right before the first ping completes — one frame of a
   * slightly-off clock beats one frame of 1970.
   */
  seed(serverAt: number) {
    if (this.synced) return;
    this.best = { rtt: 0, offset: serverAt - Date.now(), at: Date.now() };
    this.synced = true;
  }

  reset() {
    this.samples = [];
    this.best = null;
    this.rtt = 0;
    this.synced = false;
  }

  /** Server time, right now. THE function — nothing gameplay-related uses Date.now(). */
  now(): number {
    return Date.now() + (this.best?.offset ?? 0);
  }

  /**
   * The moment the player was actually looking at when they fired.
   *
   * The frame on screen was drawn from `now()` at the top of the frame, and the
   * shot message will not reach the server for another half round trip. What
   * the server needs is the former, so the client sends its own `now()` at the
   * instant of the tap — and the server clamps it into its lag-compensation
   * window regardless of what arrives.
   */
  shotTime(): number {
    return this.now();
  }
}

export const clock = new Clock();
